import { Upload } from 'lucide-react'
import { type ChangeEvent, useRef } from 'react'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { PARSER_ACCEPTED_FILE_TYPES } from '../lib/fileUpload'

interface UploadIconButtonProps {
  onUpload: (file: File) => void
  label: string
  /** The name assistive tech reads when the label alone is ambiguous, e.g. "Upload drivers" beside other rosters' "Upload". */
  ariaLabel?: string
  accept?: string
  isLoading?: boolean
  disabled?: boolean
}

/** Button that opens a file picker — used to import a file once a list already
 * has items (the full dropzone only shows in the empty state). */
export function UploadIconButton({
  onUpload,
  label,
  ariaLabel,
  accept = PARSER_ACCEPTED_FILE_TYPES,
  isLoading = false,
  disabled = false,
}: UploadIconButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (file) {
      onUpload(file)
    }
    // Reset so picking the same file again still fires onChange.
    event.target.value = ''
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || isLoading}
        aria-label={ariaLabel}
      >
        {isLoading ? <Spinner /> : <Upload />}
        {label}
      </Button>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleChange} />
    </>
  )
}
